import React, { useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { PRICE_RANGE } from '../../../constants';

type PriceDistributionChartProps = Readonly<{
  prices: ReadonlyArray<number>;
  maxPrice: number;
  numBins?: number;
  height?: number;
  barGap?: number;
  /** Bar fill color. Defaults to the light-blue tint. */
  accentColor?: string;
}>;

type PriceBin = Readonly<{
  startPrice: number;
  endPrice: number;
  count: number;
}>;

const DEFAULT_HEIGHT = 40;
const DEFAULT_NUM_BINS = 20;
const DEFAULT_BAR_GAP = 1;

export const PriceDistributionChart: React.FC<PriceDistributionChartProps> = ({
  prices,
  maxPrice: dynamicMax,
  numBins = DEFAULT_NUM_BINS,
  height = DEFAULT_HEIGHT,
  barGap = DEFAULT_BAR_GAP,
  accentColor,
}) => {
  const bins = useMemo(() => {
    if (!prices.length) return [];

    const minPrice = PRICE_RANGE.MIN;
    const maxPrice = dynamicMax;
    const binWidth = (maxPrice - minPrice) / numBins;

    const counts = new Array<number>(numBins).fill(0);
    prices.forEach(price => {
      const binIndex = Math.min(Math.floor(((price - minPrice) / (maxPrice - minPrice)) * numBins), numBins - 1);
      counts[binIndex]++;
    });

    return counts.map<PriceBin>((count, i) => ({
      startPrice: minPrice + i * binWidth,
      endPrice: minPrice + (i + 1) * binWidth,
      count,
    }));
  }, [prices, numBins, dynamicMax]);

  const maxCount = useMemo(() => Math.max(...bins.map(bin => bin.count), 1), [bins]);

  return (
    <Box
      width="100%"
      height={pxToRem(height)}
      position="relative"
    >
      <Flex
        width="100%"
        height="100%"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={pxToRem(barGap)}
      >
        {bins.map((bin, index) => (
          <Box
            key={index}
            height={`${(bin.count / maxCount) * 100}%`}
            flex={1}
            backgroundColor={accentColor ?? 'accentBlue2'}
            opacity={0.6}
            borderTopLeftRadius={pxToRem(3)}
            borderTopRightRadius={pxToRem(3)}
          />
        ))}
      </Flex>
    </Box>
  );
};
